/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W3 — ENERGY WHEEL selector static data (§33.3).
 *
 * Every constant here is VERBATIM from
 * /tmp/ksdm-phase2/django/static/mvc/component_selector/components/ewheel/view/
 * energy_wheel.selector.js (3,365). Kept out of the component so the selector
 * reads cleanly.
 *
 * Q22 naming: the selector calls it "ewheel", the form/classID call it
 * "er_wheel", the endpoints say "energy_wheels". The chassis tab + §30.4 event
 * key prefix is "energy_wheel_<id>:selector:<action>" (legacy line 992-1014).
 */

import type { DependencyGraphSpec } from './useDependencyGraph';

const MASTER = '/api/v1.0/master/v1.0.0/ahu/energy_wheels';
const CMS = '/api/v1.0/cms/1/v1.0.0/ahu/energy_wheels';

/** er_wheel CMS doc (legacy fn['current.er_wheel.cms'] = component.er_wheel.
 * model.cms urlRoot). Carries er_wheel.{manufacturer,series,mounting_arr,
 * mtr_voltage}. */
export const EW_CMS_DOC_URL = `${CMS}/params/`;
/** notes (legacy NotesCollection baseUrl). */
export const EW_NOTES_URL = `${CMS}/notes/`;
/** flow types / fan locations master collections. */
export const EW_FLOW_TYPES_URL = `${MASTER}/flow_types/`;
export const EW_FAN_LOCATIONS_URL = `${MASTER}/fan_locations/`;
/** air stream collection (legacy AirStreamCollection). */
export const EW_AIR_STREAMS_URL = '/api/v1.0/cms/1/v1.0.0/ahu/air/air_streams/';
/** pricing methods — legacy self.pricing_methods (collection/pricing_methods.js:8,
 * a GLOBAL master route, mfg-independent). The Pricing-tab Method select iterates
 * this collection verbatim (energy_wheel.selector.js render :1531). Selector-
 * parity FIX: this is the FLAT `/ahu/pricing_methods/` route (same as coil
 * :173 / uvc :55 / PHX), NOT the energy_wheels-scoped `${MASTER}/…` — the scoped
 * path 404s and left the Method dropdown empty. */
export const EW_PRICING_METHODS_URL = '/api/v1.0/master/v1.0.0/ahu/pricing_methods/';

/**
 * §32 / energy_wheel.selector.js:2532 — the erwheel MFG-id → manufacturer map.
 * (Re-exported from ratingEnvelope so the selector imports it from one place,
 * but kept named here for clarity / the spec checklist.)
 */
export { ERWHEEL_MFG_MAPPING } from './ratingEnvelope';

/** energy_wheel.selector.js:52 — mfgs that expose the WB/Humidity toggle and
 * persist BOTH wb and rh (the backend fills whichever is missing). */
export const WBRH_TOGGLE_MFG_IDS = new Set<number>([60, 233, 237]);
/** energy_wheel.selector.js:53 — initial isWbTemp state per toggle mfg. */
export const WBRH_TOGGLE_DEFAULTS: Record<number, boolean> = { 60: false, 233: true, 237: true };

/**
 * energy_wheel.selector.js:110-183 — per-mfg required fields per season, gating
 * which season blocks are SENT to the vendor (and nulled on apply when a season
 * is blank, except oa_sp/ra_sp / the air_stream ids per selectErWheel).
 */
export const SUMMER_REQUIRED_FIELDS_FOR_RATE: Record<number, string[]> = {
  60: [
    'oa_sp', 'ra_sp',
    'oa_air_stream_summer_id', 'ra_air_stream_summer_id',
    'oa_airflow_actual_summer', 'ra_airflow_actual_summer',
    'oa_db_temp_summer', 'ra_db_temp_summer',
    'oa_rh_summer', 'ra_rh_summer',
  ],
  52: [
    'oa_sp', 'ra_sp',
    'oa_air_stream_summer_id', 'ra_air_stream_summer_id',
    'oa_airflow_actual_summer', 'ra_airflow_actual_summer',
    'oa_db_temp_summer', 'ra_db_temp_summer',
    'oa_wb_temp_summer', 'ra_wb_temp_summer',
  ],
  237: [
    'oa_sp', 'ra_sp',
    'oa_air_stream_summer_id', 'ra_air_stream_summer_id',
    'oa_airflow_actual_summer', 'ra_airflow_actual_summer',
    'oa_db_temp_summer', 'ra_db_temp_summer',
  ],
};
SUMMER_REQUIRED_FIELDS_FOR_RATE[233] = SUMMER_REQUIRED_FIELDS_FOR_RATE[60];

export const WINTER_REQUIRED_FIELDS_FOR_RATE: Record<number, string[]> = {
  60: [
    'oa_sp', 'ra_sp',
    'oa_air_stream_winter_id', 'ra_air_stream_winter_id',
    'oa_airflow_actual_winter', 'ra_airflow_actual_winter',
    'oa_db_temp_winter', 'ra_db_temp_winter',
    'oa_rh_winter', 'ra_rh_winter',
  ],
  52: [
    'oa_sp', 'ra_sp',
    'oa_air_stream_winter_id', 'ra_air_stream_winter_id',
    'oa_airflow_actual_winter', 'ra_airflow_actual_winter',
    'oa_db_temp_winter', 'ra_db_temp_winter',
    'oa_wb_temp_winter', 'ra_wb_temp_winter',
  ],
  237: [
    'oa_sp', 'ra_sp',
    'oa_air_stream_winter_id', 'ra_air_stream_winter_id',
    'oa_airflow_actual_winter', 'ra_airflow_actual_winter',
    'oa_db_temp_winter', 'ra_db_temp_winter',
  ],
};
WINTER_REQUIRED_FIELDS_FOR_RATE[233] = WINTER_REQUIRED_FIELDS_FOR_RATE[60];

/** energy_wheel.selector.js:44 — fields whose option LIST + label live in the
 * er_wheel CMS doc (dot-path). Everything else is a master list (parameter_names). */
export const PARAMETER_CMS_PATHS_MAPPING: Record<string, string> = {
  mfg_id: 'er_wheel.manufacturer',
  series_id: 'er_wheel.series',
  mounting_arr_id: 'er_wheel.mounting_arr',
  mtr_voltage_id: 'er_wheel.mtr_voltage',
};

/** energy_wheel.selector.js:527-555 — field → master-list KEY (the
 * master/v1.0.0/ahu/energy_wheels/<key>/ branch). Fields NOT here AND not in
 * PARAMETER_CMS_PATHS_MAPPING use the cms params branch
 * (cms/.../energy_wheels/params/<field>/, mapToCMS). */
export const PARAMETER_NAMES: Record<string, string> = {
  configuration_id: 'configuration',
  rotor_style_id: 'rotor_style',
  drive_belt_id: 'drive_belt',
  fin_thickness_id: 'fin_thickness',
  instl_position_id: 'instl_position',
  rotor_depth_id: 'rotor_depth',
  seal_id: 'seal',
  hub_id: 'hub',
  well_height_id: 'well_height',
  casing_id: 'casing',
  casing_type_id: 'casing_type',
  purge_sector_location_id: 'purge_sector_location',
  type_id: 'type',
  certification_id: 'certification',
  coating_frame_id: 'coating_frame',
  media_edge_id: 'media_edge',
  mtr_service_door_id: 'mtr_service_door',
  bottom_plate_id: 'bottom_plate',
  coating_media_id: 'coating_media',
  speed_control_id: 'speed_control',
  purge_angle_id: 'purge_angle',
  desiccant_id: 'desiccant',
  wheel_size_id: 'wheel_size',
  fin_pattern_id: 'fin_pattern',
  temp_control_id: 'temp_control',
  rotation_detector_id: 'rotation_detector',
};

/**
 * ★ EW RATE ENUM MAPS (pre-existing rate-contract fix). The vendor rate handlers
 * validate `type`, `flow_type` and `fin_pattern` as STRING enums — innergy_tech
 * data_transformer.py:136-145 (type∈{total,sensible}, flow_type∈{counter,
 * parallel}, fin_pattern∈{high_performance,…}); the heatex microservice rejects a
 * numeric `type` with a 400. Legacy sends the master row's `.value`
 * (dataForRating → `model.value`, energy_wheel.selector.js:1271). The React port
 * fell back to the raw numeric id whenever the master list wasn't cached — and
 * the header-mode auto-fire builds the rate input BEFORE the dependency cascade
 * has populated `masterLists`, so that fallback fired on essentially every rate
 * (proven live: type/flow_type/fin_pattern shipped as ids → innergy is_valid()
 * fails → the backend `set`-crash; heatex 400). These id→enum maps are the
 * reliable resolution; ids captured from /master/…/{type,flow_types,fin_pattern}/
 * on dev3. */
export const EW_TYPE_ENUM: Record<string, string> = { 1: 'total', 2: 'sensible' };
export const EW_FLOW_TYPE_ENUM: Record<string, string> = { 1: 'counter', 2: 'parallel' };
export const EW_FIN_PATTERN_ENUM: Record<string, string> = {
  1: 'standard',
  2: 'high_performance',
  3: 'low_pressure',
};

/** Resolve an EW enum field to the vendor's STRING value: prefer the loaded
 * master row's `.value` (legacy parity), else the deterministic id→enum map.
 * NEVER returns the bare numeric id (which the vendor rate handlers reject). */
export function ewRateEnumValue(
  rows: Array<Record<string, unknown>> | undefined,
  id: unknown,
  enumMap: Record<string, string>
): unknown {
  if (id == null || id === '') return id;
  const row = rows?.find((r) => String(r.id ?? r.value) === String(id));
  const v = row?.value;
  if (typeof v === 'string' && v !== '') return v;
  return enumMap[String(id)] ?? id;
}

/**
 * energy_wheel.selector.js:556-927 — the dependency graph passed to
 * DependenciesHandler. Compacted to the {dependencies, default_values} shape
 * useDependencyGraph consumes. mfg_id → the full descendant set; series_id →
 * the same minus mfg; the leaf params have no dependencies. default_values are
 * the legacy fallback ids (mfg 233/60/52/237; series 828/1306/274).
 */
const EW_DESCENDANTS = [
  'series_id', 'type_id', 'desiccant_id', 'wheel_size_id', 'rotor_style_id',
  'instl_position_id', 'configuration_id', 'seal_id', 'hub_id', 'well_height_id',
  'casing_id', 'casing_type_id', 'purge_sector_location_id', 'rotor_depth_id',
  'purge_angle_id', 'fin_pattern_id', 'certification_id', 'speed',
  'speed_control_id', 'mounting_arr_id', 'fin_thickness_id', 'drive_belt_id',
  'temp_control_id', 'rotation_detector_id', 'coating_frame_id', 'media_edge_id',
  'mtr_service_door_id', 'bottom_plate_id', 'coating_media_id',
];

export const EW_DEPENDENCY_GRAPH: DependencyGraphSpec = {
  mfg_id: {
    dependencies: EW_DESCENDANTS,
    default_values: [233, 60, 52, 237],
  },
  series_id: {
    dependencies: EW_DESCENDANTS.filter((d) => d !== 'series_id'),
    default_values: [828, 1306, 274],
  },
  // leaf params carry no children (their lists narrow when mfg/series reload).
};

/**
 * ★ Phase 6.5 (builder 3) — the HEADER-ONLY spec: `EW_DEPENDENCY_GRAPH` plus the
 * per-LEAF `default_values` transcribed VERBATIM from
 * energy_wheel.selector.js:662-920. The board spec above is left byte-identical
 * (a separate object, still what the board host passes), so no board dialog's
 * option-picking can change.
 *
 * Why the header host needs them: without a leaf's `default_values` the engine
 * can only auto-select a SOLE option (useDependencyGraph applyNewValues), so
 * every EW select whose narrowed list has 2+ rows stayed blank — type_id,
 * rotor_style_id, configuration_id, purge_angle_id, coating_media_id,
 * rotation_detector_id — while legacy picks the first default that survives
 * narrowing (its `setDefaultsIfNullChecker` passes because REQUIRED_FIELDS[233]
 * lists all of them, js:191-280).
 *
 * Verified against a first-ever legacy open (mfg 233 / series 1306): type_id 1
 * Total · rotor_style_id 1 Non-Sectioned · configuration_id 1 · purge_angle_id 6
 * · desiccant_id 1 (2 not offered → the [2,1] tail wins) · fin_pattern_id 1 ·
 * speed 20 ([12,20] → 12 absent) · well_height_id 3 ·
 * purge_sector_location_id 6 · drive_belt_id 2 · fin_thickness_id 2 ·
 * instl_position_id 1 · coating_media_id 1 · rotation_detector_id 1 ·
 * media_edge_id 1 · mtr_service_door_id 1 · bottom_plate_id 1 ·
 * coating_frame_id 1 · temp_control_id 1 · speed_control_id 2 · casing_id 1 ·
 * casing_type_id 1 · seal_id 1 · hub_id 1 · certification_id 2 (its [1] is
 * absent, so the sole remaining option wins).
 */
export const EW_DEPENDENCY_GRAPH_HEADER: DependencyGraphSpec = {
  ...EW_DEPENDENCY_GRAPH,
  type_id: { dependencies: [], default_values: [1] },
  wheel_size_id: { dependencies: ['mounting_arr_id'], default_values: [] },
  rotor_style_id: { dependencies: [], default_values: [1, 2, 3, 4] },
  instl_position_id: { dependencies: [], default_values: [1, 2] },
  drive_belt_id: { dependencies: [], default_values: [2, 3] },
  fin_thickness_id: { dependencies: [], default_values: [2] },
  configuration_id: {
    dependencies: ['purge_sector_location_id'],
    default_values: [1, 2],
  },
  seal_id: { dependencies: [], default_values: [1, 2] },
  hub_id: { dependencies: [], default_values: [1] },
  well_height_id: { dependencies: [], default_values: [3, 6] },
  purge_sector_location_id: { dependencies: [], default_values: [6, 2, 1] },
  rotor_depth_id: { dependencies: [], default_values: [] },
  casing_id: { dependencies: [], default_values: [1] },
  casing_type_id: { dependencies: [], default_values: [1] },
  desiccant_id: { dependencies: [], default_values: [2, 1] },
  purge_angle_id: { dependencies: [], default_values: [6, 1, 4] },
  fin_pattern_id: { dependencies: [], default_values: [2, 1] },
  certification_id: { dependencies: [], default_values: [1] },
  fan_locations_id: { dependencies: [], default_values: [1] },
  mounting_arr_id: { dependencies: [], default_values: [1] },
  flow_type_id: { dependencies: [], default_values: [1] },
  mtr_voltage_id: { dependencies: [], default_values: [1] },
  speed: { dependencies: [], default_values: [12, 20] },
  speed_control_id: { dependencies: [], default_values: [2] },
  temp_control_id: { dependencies: [], default_values: [1] },
  rotation_detector_id: { dependencies: [], default_values: [1] },
  coating_frame_id: { dependencies: [], default_values: [1] },
  media_edge_id: { dependencies: [], default_values: [1] },
  mtr_service_door_id: { dependencies: [], default_values: [1] },
  bottom_plate_id: { dependencies: [], default_values: [1] },
  coating_media_id: { dependencies: [], default_values: [1] },
};

/**
 * energy_wheel.selector.js:191-280 — per-mfg REQUIRED_FIELDS (Sets). Used by
 * __checkParents (skip a null parent when it's not required) + the #rate gate.
 * mfg 60's full set; 95's smaller set; 233/237/52 alias 60.
 */
const EW_REQUIRED_60 = new Set<string>([
  'elevation', 'mfg_id', 'type_id', 'series_id', 'desiccant_id', 'purge_angle_id',
  'fin_pattern_id', 'mounting_arr_id', 'speed_control_id', 'speed', 'mtr_voltage_id',
  'fan_locations_id', 'flow_type_id', 'certification_id', 'oa_sp', 'ra_sp',
  'oa_air_stream_summer_id', 'ra_air_stream_summer_id', 'oa_airflow_actual_summer',
  'ra_airflow_actual_summer', 'oa_db_temp_summer', 'ra_db_temp_summer',
  'oa_rh_summer', 'ra_rh_summer', 'oa_air_stream_winter_id', 'ra_air_stream_winter_id',
  'oa_airflow_actual_winter', 'ra_airflow_actual_winter', 'oa_db_temp_winter',
  'ra_db_temp_winter', 'oa_rh_winter', 'ra_rh_winter', 'rotor_style_id', 'seal_id',
  'casing_id', 'casing_type_id', 'well_height_id', 'configuration_id',
  'instl_position_id', 'purge_sector_location_id', 'drive_belt_id', 'fin_thickness_id',
  'temp_control_id', 'rotation_detector_id', 'coating_frame_id', 'media_edge_id',
  'mtr_service_door_id', 'bottom_plate_id', 'coating_media_id',
]);
const EW_REQUIRED_95 = new Set<string>([
  'mfg_id', 'type_id', 'series_id', 'desiccant_id', 'purge_angle_id', 'fin_pattern_id',
  'mounting_arr_id', 'speed_control_id', 'speed', 'mtr_voltage_id', 'fan_locations_id',
  'flow_type_id', 'certification_id', 'oa_air_stream_summer_id', 'ra_air_stream_summer_id',
  'oa_airflow_actual_summer', 'ra_airflow_actual_summer', 'oa_db_temp_summer',
  'ra_db_temp_summer', 'oa_rh_summer', 'ra_rh_summer', 'oa_air_stream_winter_id',
  'ra_air_stream_winter_id', 'oa_airflow_actual_winter', 'ra_airflow_actual_winter',
  'oa_db_temp_winter', 'ra_db_temp_winter', 'oa_rh_winter', 'ra_rh_winter',
]);
export const EW_REQUIRED_FIELDS: Record<number, Set<string>> = {
  60: EW_REQUIRED_60,
  95: EW_REQUIRED_95,
  233: EW_REQUIRED_60,
  237: EW_REQUIRED_60,
  52: EW_REQUIRED_60,
};

/** energy_wheel.selector.js:331-449 — the new-EW default_values (subset that
 * seeds a blank state; for a SAVED ewheel the model fields override these). */
export const EW_DEFAULTS: Record<string, unknown> = {
  min_face_velocity: 200,
  max_face_velocity: 800,
  oa_sp: 0.0,
  ra_sp: 0.0,
  flow_type_id: 1,
  oa_air_stream_summer_id: 1,
  ra_air_stream_summer_id: 2,
  oa_air_stream_winter_id: 1,
  ra_air_stream_winter_id: 2,
  difference_in_pressure_winter: 1.5,
  difference_in_pressure_summer: 1.5,
  rotor_depth_id: 8,
  pricing_method_id: 1,
};

/**
 * `updateDefaultsDependsOnMFG` (energy_wheel.selector.js:2247-2290) — the
 * per-manufacturer pins `changeSearchField` writes into `self.state` on the
 * `mfg_id` branch (js:2442), BEFORE the cascade at js:2476.
 *
 * They matter because the graph's leaf `default_values` above are AI-Rotor
 * shaped: `hub_id [1]` (Standard) and `speed_control_id [2]` (VFD) survive
 * mfg 233's narrowed lists but not Heatex's (mfg 52 offers hub [2,3] and
 * speed control [4,5]). With no default surviving and more than one option,
 * `applyParameterNewValues` clears the field — which is why picking Heatex on
 * dev3 blanked Rotor Hub and Speed Control where dev fills them with
 * `Ball bearing with shaft` / `Constant drive`. Applied first, these values
 * are already valid members of the narrowed list, so the cascade keeps them.
 *
 * Legacy applies this ONLY on a manufacturer change (and on `#reset`, which
 * ends in `$select.trigger('change')` on the mfg select) — never on open, where
 * a saved wheel's own params are the seed. Ported with the same scope.
 */
export const EW_MFG_DEFAULTS: Record<number, Record<string, unknown>> = {
  // AI Rotor
  233: {
    hub_id: 1,
    drive_belt_id: 2,
    speed_control_id: 2,
    speed: 12,
    seal_id: 1,
    rotor_depth_id: 8,
    purge_sector_location_id: 6,
    purge_angle_id: 6,
  },
  // Annexair
  237: {
    desiccant_id: 2,
    purge_sector_location_id: 6,
    purge_angle_id: 10,
  },
  // Heatex
  52: {
    hub_id: 2,
    drive_belt_id: 2,
    well_height_id: 6,
    speed_control_id: 5,
    configuration_id: 10,
    speed: 25,
    seal_id: 2,
    rotor_depth_id: 2,
    purge_sector_location_id: 1,
    purge_angle_id: 1,
  },
  // Innergytech
  60: {
    hub_id: 1,
    drive_belt_id: 3,
    speed_control_id: 2,
    speed: 20,
    seal_id: 1,
    rotor_depth_id: null,
    fin_pattern_id: 2,
    purge_sector_location_id: 1,
    purge_angle_id: 1,
  },
};

/**
 * The second half of `updateDefaultsDependsOnMFG` (js:2291-2292): the manufacturer
 * also swaps `purge_sector_location_id`'s own `default_values` on the live graph,
 * Annexair getting a longer preference list than everyone else. Legacy does the
 * same swap at open for mfg 237 (js:1159-1160).
 */
export const EW_PURGE_SECTOR_DEFAULTS = (mfgId: number | null | undefined): number[] =>
  Number(mfgId) === 237 ? [6, 7, 8, 9, 2, 1] : [6, 2, 1];

/** the .model_field input names per season (the StreamPairBlock rows). Used to
 * sweep model fields for selectErWheel's inputsDOM loop. */
export const EW_SEASON_MODEL_FIELDS = (season: 'summer' | 'winter'): string[] => [
  `oa_air_stream_${season}_id`, `ra_air_stream_${season}_id`,
  `oa_airflow_actual_${season}`, `ra_airflow_actual_${season}`,
  `oa_db_temp_${season}`, `ra_db_temp_${season}`,
  `oa_wb_temp_${season}`, `ra_wb_temp_${season}`,
  `oa_rh_${season}`, `ra_rh_${season}`,
];

/**
 * Build the per-parameter option-list URL (energy_wheel.selector.js loadParams).
 *  - master branch:  master/v1.0.0/ahu/energy_wheels/<parameter_key>/<filters>
 *  - cms branch:     cms/.../energy_wheels/params/<field>/<filters>  (mapToCMS)
 * `parents` is the field's parent filters as `name=value&...`; `divider` is the
 * configuration_id tunnel-divider filter.
 */
export function ewParamUrl(field: string, filters: string): { url: string; mapToCms: boolean } {
  const key = PARAMETER_NAMES[field];
  const qs = filters ? `?${filters}` : '';
  if (key) return { url: `${MASTER}/${key}/${qs}`, mapToCms: false };
  // mapToCMS branch (note: the relative URL is standardized by uni_tools; here
  // the full /api/v1.0/ prefix is added).
  return { url: `/api/v1.0/cms/1/v1.0.0/ahu/energy_wheels/params/${field}/${qs}`, mapToCms: true };
}

/** energy_wheel speeds additionalLoadable (gated mfg+series) — parity with the
 * EW FORM (cms/.../energy_wheels/params/speed/?mfg_id&series_id). */
export const ewSpeedUrl = (mfgId: number | string, seriesId: number | string) =>
  `${CMS}/params/speed/?mfg_id=${mfgId}&series_id=${seriesId}`;
